import {IBrowserUtilsService} from '@techsee/techsee-client-infra/lib/services/BrowserUtilsService';
import last from 'lodash/last';

/**
 * Extended interface that adds setParamValue, setParamValues, and extractFileName
 * to the base IBrowserUtilsService. These methods exist on Angular's TsUrlUtilsService
 * but are missing from BrowserUtilsService in client-infra.
 *
 * This is a temporary wrapper until these methods are added to client-infra.
 */
export interface IBrowserUtilsExtended extends IBrowserUtilsService {
    setParamValue(param: string, value: string | null): void;
    setParamValues(params: Record<string, string | null>, replace?: boolean): void;
    extractFileName(url: string): string | undefined;
    cleanParams(params: Record<string, any>): Record<string, any>;
}

/**
 * Wraps BrowserUtilsService and adds URL param manipulation + extractFileName.
 * Delegates all original IBrowserUtilsService methods to the wrapped instance.
 */
export class BrowserUtilsExtended implements IBrowserUtilsExtended {
    constructor(private _delegate: IBrowserUtilsService) {}

    // --- Delegated IBrowserUtilsService methods ---

    get isLocalStorageSupported(): boolean {
        return this._delegate.isLocalStorageSupported;
    }

    getCurrentOrigin(): string {
        return this._delegate.getCurrentOrigin();
    }

    getParamValue(param: string): string {
        return this._delegate.getParamValue(param);
    }

    getHashValue(param: string): string {
        return this._delegate.getHashValue(param);
    }

    getCurrentHostName(): string {
        return this._delegate.getCurrentHostName();
    }

    redirect(url: string): void {
        this._delegate.redirect(url);
    }

    openInNewWindow(url: string): void {
        this._delegate.openInNewWindow(url);
    }

    saveToLocalStorage(key: string, data: any): void {
        this._delegate.saveToLocalStorage(key, data);
    }

    getFromLocalStorage(key: string): any {
        return this._delegate.getFromLocalStorage(key);
    }

    removeFromLocalStorage(key: string): void {
        this._delegate.removeFromLocalStorage(key);
    }

    saveToSessionStorage(key: string, data: any): void {
        this._delegate.saveToSessionStorage(key, data);
    }

    getFromSessionStorage(key: string): any {
        return this._delegate.getFromSessionStorage(key);
    }

    removeFromSessionStorage(key: string): void {
        this._delegate.removeFromSessionStorage(key);
    }

    // --- Extended methods ---

    /**
     * Set a single URL query parameter value.
     * Updates the browser URL without triggering navigation.
     * Mirrors Angular's $location.search(param, value).
     */
    setParamValue(param: string, value: string | null): void {
        const url = new URL(window.location.href);

        if (value === null || value === undefined) {
            url.searchParams.delete(param);
        } else {
            url.searchParams.set(param, String(value));
        }

        window.history.replaceState(null, '', url.pathname + url.search);
    }

    /**
     * Set multiple URL query parameters simultaneously.
     * When replace=true, all existing params are replaced with the new ones.
     * When replace=false (default), new params are merged with existing ones.
     * Mirrors Angular's TsUrlUtilsService.setParamValues().
     */
    setParamValues(params: Record<string, string | null>, replace?: boolean): void {
        const url = new URL(window.location.href);

        if (replace) {
            // Clear all existing params and set only the new ones
            const keys = Array.from(url.searchParams.keys());

            keys.forEach((key) => url.searchParams.delete(key));
        }

        Object.entries(params).forEach(([key, value]) => {
            if (value === null || value === undefined) {
                url.searchParams.delete(key);
            } else {
                url.searchParams.set(key, String(value));
            }
        });

        window.history.replaceState(null, '', url.pathname + url.search);
    }

    /**
     * Extract filename from a URL path.
     * Mirrors Angular's TsUrlUtilsService.extractFileName().
     */
    extractFileName(url: string): string | undefined {
        try {
            const parsedUrl = new URL(url, window.location.origin);

            return last(parsedUrl.pathname.split('/'));
        } catch {
            return undefined;
        }
    }

    cleanParams(params: Record<string, any>): Record<string, any> {
        const cleaned: Record<string, any> = {};

        for (const key in params) {
            if (params[key] !== undefined && params[key] !== null) {
                cleaned[key] = params[key];
            }
        }

        return Object.keys(cleaned).length > 0 ? cleaned : {};
    }
}
